import React, { useState, useEffect } from "react";

/**
 * Analytics Debugger Component
 * Helps troubleshoot Google Analytics and other tracking issues
 */
const AnalyticsDebugger = () => {
  const [debugInfo, setDebugInfo] = useState({
    gtag: false,
    dataLayer: false,
    gaLoaded: false,
    gtagScript: false,
    pageViews: 0,
    errors: [],
    events: [],
  });

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Only show in development or when debug mode is enabled
    const shouldShow =
      import.meta.env.DEV ||
      import.meta.env.VITE_DEBUG_MODE === "true" ||
      localStorage.getItem("analytics-debug") === "true";

    setIsVisible(shouldShow);

    if (shouldShow) {
      checkAnalyticsStatus();

      // Monitor gtag calls
      interceptGtagCalls();
    }
  }, []);

  const checkAnalyticsStatus = () => {
    const info = {
      gtag: typeof window.gtag === "function",
      dataLayer: Array.isArray(window.dataLayer),
      gaLoaded: false,
      gtagScript: false,
      pageViews: debugInfo.pageViews,
      errors: [...debugInfo.errors],
      events: [...debugInfo.events],
    };

    // Check if GA script is loaded
    const gtagScript = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js"]`,
    );
    info.gtagScript = !!gtagScript;

    // Check if GA is actually loaded
    if (window.gtag && window.dataLayer) {
      info.gaLoaded = window.dataLayer.some(
        (item) => item && typeof item === "object" && item[0] === "config",
      );
    }

    setDebugInfo(info);
  };

  const interceptGtagCalls = () => {
    if (window.gtag) {
      const originalGtag = window.gtag;
      window.gtag = function (...args) {
        // Log the call
        const timestamp = new Date().toLocaleTimeString();
        const event = {
          timestamp,
          type: args[0],
          data: args.slice(1),
          args: args,
        };

        setDebugInfo((prev) => ({
          ...prev,
          events: [...prev.events.slice(-9), event], // Keep last 10 events
          pageViews:
            args[0] === "event" && args[1] === "page_view"
              ? prev.pageViews + 1
              : prev.pageViews,
        }));

        // Call original function
        return originalGtag.apply(this, args);
      };
    }
  };

  const testPageView = () => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: "Test Page View",
        page_location: window.location.href,
        page_path: window.location.pathname,
        custom_parameter: "debug_test",
      });
    } else {
      setDebugInfo((prev) => ({
        ...prev,
        errors: [...prev.errors, "gtag function not available"],
      }));
    }
  };

  const testCustomEvent = () => {
    if (window.gtag) {
      window.gtag("event", "debug_test", {
        event_category: "debug",
        event_label: "manual_test",
        value: 1,
      });
    }
  };

  const clearLogs = () => {
    setDebugInfo((prev) => ({
      ...prev,
      events: [],
      errors: [],
      pageViews: 0,
    }));
  };

  const copyDebugInfo = () => {
    const info = {
      environment: {
        NODE_ENV: import.meta.env.NODE_ENV,
        MODE: import.meta.env.MODE,
        VITE_GA_ID: import.meta.env.VITE_GA_ID,
        VITE_ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS,
        VITE_DEBUG_MODE: import.meta.env.VITE_DEBUG_MODE,
      },
      analytics: debugInfo,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    navigator.clipboard.writeText(JSON.stringify(info, null, 2));
    alert("Debug info copied to clipboard!");
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        width: "400px",
        maxHeight: "600px",
        backgroundColor: "#1a1a1a",
        color: "#fff",
        border: "1px solid #333",
        borderRadius: "8px",
        padding: "16px",
        fontSize: "12px",
        fontFamily: "monospace",
        zIndex: 9999,
        overflow: "auto",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h3 style={{ margin: 0, color: "#4ade80", fontSize: "14px" }}>
          📊 Analytics Debug
        </h3>
        <button
          onClick={() => setIsVisible(false)}
          style={{
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: "10px",
          }}
        >
          ✕
        </button>
      </div>

      {/* Status */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{ color: "#94a3b8", marginBottom: "8px", fontWeight: "bold" }}
        >
          Status
        </div>
        <div style={{ display: "grid", gap: "4px" }}>
          <div>
            <span
              style={{ color: debugInfo.gtagScript ? "#22c55e" : "#ef4444" }}
            >
              {debugInfo.gtagScript ? "✅" : "❌"}
            </span>{" "}
            gtag script loaded
          </div>
          <div>
            <span style={{ color: debugInfo.gtag ? "#22c55e" : "#ef4444" }}>
              {debugInfo.gtag ? "✅" : "❌"}
            </span>{" "}
            gtag function available
          </div>
          <div>
            <span
              style={{ color: debugInfo.dataLayer ? "#22c55e" : "#ef4444" }}
            >
              {debugInfo.dataLayer ? "✅" : "❌"}
            </span>{" "}
            dataLayer available
          </div>
          <div>
            <span style={{ color: debugInfo.gaLoaded ? "#22c55e" : "#ef4444" }}>
              {debugInfo.gaLoaded ? "✅" : "❌"}
            </span>{" "}
            GA configured
          </div>
        </div>
      </div>

      {/* Environment */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{ color: "#94a3b8", marginBottom: "8px", fontWeight: "bold" }}
        >
          Environment
        </div>
        <div style={{ fontSize: "10px" }}>
          <div>GA ID: {import.meta.env.VITE_GA_ID || "Not set"}</div>
          <div>
            Analytics Enabled:{" "}
            {import.meta.env.VITE_ENABLE_ANALYTICS || "Not set"}
          </div>
          <div>Mode: {import.meta.env.MODE}</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{ color: "#94a3b8", marginBottom: "8px", fontWeight: "bold" }}
        >
          Statistics
        </div>
        <div>Page Views Tracked: {debugInfo.pageViews}</div>
        <div>Total Events: {debugInfo.events.length}</div>
        <div>Errors: {debugInfo.errors.length}</div>
      </div>

      {/* Controls */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{ color: "#94a3b8", marginBottom: "8px", fontWeight: "bold" }}
        >
          Test Controls
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <button
            onClick={testPageView}
            style={{
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: "10px",
            }}
          >
            Test Page View
          </button>
          <button
            onClick={testCustomEvent}
            style={{
              background: "#8b5cf6",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: "10px",
            }}
          >
            Test Event
          </button>
          <button
            onClick={copyDebugInfo}
            style={{
              background: "#059669",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: "10px",
            }}
          >
            Copy Debug
          </button>
          <button
            onClick={clearLogs}
            style={{
              background: "#dc2626",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: "10px",
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Recent Events */}
      <div style={{ marginBottom: "12px" }}>
        <div
          style={{ color: "#94a3b8", marginBottom: "8px", fontWeight: "bold" }}
        >
          Recent Events
        </div>
        <div
          style={{
            maxHeight: "150px",
            overflow: "auto",
            backgroundColor: "#0f0f0f",
            padding: "8px",
            borderRadius: "4px",
            fontSize: "10px",
          }}
        >
          {debugInfo.events.length === 0 ? (
            <div style={{ color: "#6b7280" }}>No events yet...</div>
          ) : (
            debugInfo.events.slice(-5).map((event, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "4px",
                  borderBottom: "1px solid #333",
                  paddingBottom: "4px",
                }}
              >
                <div style={{ color: "#4ade80" }}>
                  {event.timestamp} - {event.type}
                </div>
                <div style={{ color: "#94a3b8", marginLeft: "8px" }}>
                  {JSON.stringify(event.data, null, 2)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Errors */}
      {debugInfo.errors.length > 0 && (
        <div>
          <div
            style={{
              color: "#ef4444",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            Errors
          </div>
          <div
            style={{
              backgroundColor: "#4c1d1d",
              padding: "8px",
              borderRadius: "4px",
              fontSize: "10px",
            }}
          >
            {debugInfo.errors.map((error, index) => (
              <div key={index} style={{ color: "#fca5a5" }}>
                {error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={{ marginTop: "12px", fontSize: "10px", color: "#6b7280" }}>
        <div>💡 Enable in localStorage: analytics-debug = true</div>
        <div>🔍 Check Network tab for gtag requests</div>
        <div>📊 GA Real-time reports should show activity</div>
      </div>
    </div>
  );
};

export default AnalyticsDebugger;
